/**
 * SoulReviewPanel — ADR-150 D3/D4/D5. What this draft does to the core layers
 * above it.
 *
 * A composed SOUL is four layers (ADR-015) and the fleet authors the last one.
 * Most of what goes there is exactly right: an identity, a tone, a routing table.
 * Three sections are core's, and this panel is where an operator finds that out
 * while typing rather than when Propose refuses them.
 *
 * THREE STATES, and keeping them distinct is the whole job:
 *
 *   blocking     — the draft REDEFINES a governed section. Propose is refused,
 *                  and the panel quotes the core line, because ADR-150 D4 only
 *                  permits a block that can cite one.
 *   advisory     — the draft NAMES a governed rule in prose. Ships fine. Every
 *                  fleet's brain does this today; saying so is a courtesy, not a
 *                  complaint.
 *   unavailable  — the review could not run. NOT "clean". A review that reports
 *                  no objections because it could not read the rules is the
 *                  failure ADR-149 D2b named, so this renders as loudly as a
 *                  finding does.
 *
 * Silent on a clean draft, deliberately: ADR-136 D4's "silence on what is
 * correct" — a panel that always says something is a panel operators stop
 * reading.
 */
import type { SoulReviewResult } from '../api/client'

export function SoulReviewPanel({ review }: { review: SoulReviewResult | null }) {
  if (!review) return null

  if (review.unavailable) {
    return (
      <div className="rounded border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900 space-y-1">
        <p className="font-medium">Not reviewed against core</p>
        <p>{review.unavailable}</p>
        <p className="text-amber-800">
          This is not a pass — the draft has not been checked for conflicts with core's governed
          sections, and Propose will refuse it for the same reason.
        </p>
      </div>
    )
  }

  // `?? []` is not defensive clutter — it is the fix for a live crash.
  //
  // The server now guarantees a non-nil array, but this component died on
  // `findings: null` for every clean draft ("can't access property filter,
  // e.findings is null") and took the whole editor behind the error boundary. A
  // rendering component should not be the thing that decides whether a payload
  // was well-formed, and an older MC serving a newer frontend is a real state
  // during a rollout.
  const findings = review.findings ?? []
  const blocking = findings.filter(f => f.blocking)
  const advisory = findings.filter(f => !f.blocking)
  if (blocking.length === 0 && advisory.length === 0) return null

  return (
    <div className="space-y-2">
      {blocking.length > 0 && (
        <div className="rounded border border-red-300 bg-red-50 px-3 py-2 text-xs text-red-900 space-y-2">
          <p className="font-medium">
            {blocking.length === 1 ? 'This redefines a section core governs' : `This redefines ${blocking.length} sections core governs`}
            {' — it cannot be proposed'}
          </p>
          {blocking.map((f, i) => (
            <div key={i} className="space-y-0.5">
              <p>
                <span className="font-mono">line {f.draft_line}</span>: <span className="font-mono">{f.draft_text}</span>
              </p>
              {/* The citation. D4 permits no block without one, so if this is ever
                  empty the bug is server-side, not a judgement call. */}
              <p className="text-red-800">
                core already defines <span className="font-medium">{f.section}</span> at{' '}
                <span className="font-mono">{f.core_source}{f.core_line ? `:${f.core_line}` : ''}</span>
              </p>
            </div>
          ))}
          <p className="text-red-800">
            Remove the heading and core's section stands — it is composed above yours at deploy, so you
            do not need to restate it. If the core rule is wrong for every fleet, that is a core change.
          </p>
        </div>
      )}

      {advisory.length > 0 && (
        <div className="rounded border border-gray-200 bg-gray-50 px-3 py-2 text-xs text-gray-700 space-y-1">
          <p className="font-medium text-gray-800">
            {advisory.length === 1 ? 'One line restates a core rule' : `${advisory.length} lines restate a core rule`}
            {' — fine to ship'}
          </p>
          {advisory.map((f, i) => (
            <p key={i}>
              <span className="font-mono">line {f.draft_line}</span> echoes{' '}
              <span className="font-medium">{f.section}</span>, which core states at{' '}
              <span className="font-mono">{f.core_source}{f.core_line ? `:${f.core_line}` : ''}</span>.
            </p>
          ))}
          <p className="text-gray-600">
            You do not need to — and if you have reworded it, your wording is now a second, drifting
            copy of a rule core owns.
          </p>
        </div>
      )}
    </div>
  )
}
